import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, PlusIcon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { createConversation, listConversations, listProjects } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { PageHeader } from '../../components/portal/PageHeader';
import { MessageThread } from '../../components/portal/MessageThread';
import { Dialog } from '../../components/portal/Dialog';
import { EmptyState, InlineError, QueryState } from '../../components/portal/QueryState';
import { formatRelative } from '../../utils/portalFormat';
export function PortalMessages() {
  const {
    orgId = ''
  } = useParams<{
    orgId: string;
  }>();
  const [params, setParams] = useSearchParams();
  const {
    roleIn
  } = usePortalAuth();
  const role = roleIn(orgId);
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listConversations(orgId), [orgId]);
  const [composing, setComposing] = React.useState(false);
  const selectedId = params.get('c');
  const selected = data?.find(c => c.id === selectedId) ?? null;
  return <div data-id="element-3081" data-mp="3hm4iqto62lki">
      <PageHeader eyebrow={orgName(orgId)} title="Messages" description="Conversations with the Cavalla team, one per project plus anything else you raise." actions={can(role, 'messages.send') && <Button size="sm" onClick={() => setComposing(true)} data-id="element-3083" data-mp="33t6fkl5hfp6e">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-3084" data-mp="2ysr3dwwxsnq0" />
              New conversation
            </Button>} data-id="element-3082" data-mp="2tu7dxmj4wtsw" />

      <div className="mt-6" data-id="element-3085" data-mp="j0l4qpchrorw">
        <QueryState loading={loading} error={error} data-id="element-3086" data-mp="j3z4rqxonlw6">
          {data && data.length === 0 ? <EmptyState title="No conversations yet" body="Start one and the team will reply here." data-id="element-3087" data-mp="e5fi1ob9m62r" /> : <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-id="element-3088" data-mp="q52shicqvkth">
              <ul className={`divide-y divide-line self-start rounded-md border border-line ${selected ? 'hidden lg:block' : ''}`} aria-label="Conversations" data-id="element-3089" data-mp="q7fimisnp1hm">
                {data?.map(c => <li key={c.id} data-id="element-3090" data-mp="101fud3ig14zb">
                    <button type="button" onClick={() => setParams({
                c: c.id
              })} aria-current={c.id === selectedId ? 'true' : undefined} className={`flex w-full items-start gap-3 px-4 py-4 text-left transition-colors duration-150 ${c.id === selectedId ? 'bg-panel' : 'hover:bg-panel/60'}`} data-id="element-3091" data-mp="2g1me1rdwlrd">
                      <span className="min-w-0 flex-1" data-id="element-3092" data-mp="3fv6e7a3bcaim">
                        <span className={`block truncate text-[15px] ${c.unreadCount ? 'font-medium text-ink' : 'text-body'}`} data-id="element-3093" data-mp="3fuekk4ta0muz">{c.subject}</span>
                        {c.lastMessage && <span className="mt-0.5 block truncate text-[13px] text-muted" data-id="element-3094" data-mp="3d971nhkizprf">{c.lastMessage.body}</span>}
                        <span className="mt-1 block text-[12px] text-muted" data-id="element-3095" data-mp="38cgkeqwzio4f">
                          {c.projectId ? 'Project' : 'General'}, {formatRelative(c.updatedAt)}
                        </span>
                      </span>
                      {c.unreadCount > 0 && <span className="mt-0.5 rounded-full bg-gold px-2 text-[12px] font-bold text-charcoal" data-id="element-3096" data-mp="2ubmfii95b5ai">
                          {c.unreadCount}
                          <span className="sr-only" data-id="element-3097" data-mp="2pit63h2qf8s3"> unread</span>
                        </span>}
                    </button>
                  </li>)}
              </ul>

              <section className={`min-w-0 rounded-md border border-line p-5 sm:p-6 ${selected ? '' : 'hidden lg:block'}`} aria-label={selected ? selected.subject : 'Conversation'} data-id="element-3098" data-mp="2andl01k4qn7u">
                {selected ? <>
                    <button type="button" onClick={() => setParams({})} className="mb-4 inline-flex items-center gap-1 text-[14px] text-muted hover:text-ink lg:hidden" data-id="element-3099" data-mp="2vdh65hze6huu">
                      <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-3100" data-mp="2mkdw7jnygn4y" />
                      All conversations
                    </button>
                    <h2 className="mb-6 font-display text-[20px] text-ink" data-id="element-3101" data-mp="2qk68o7ggzdfl">{selected.subject}</h2>
                    <MessageThread orgId={orgId} conversationId={selected.id} canSend={can(role, 'messages.send')} data-id="element-3102" data-mp="2cl6giptr0geo" />
                  </> : <p className="py-16 text-center text-[15px] text-muted" data-id="element-3103" data-mp="23ljjshbyryry">Choose a conversation.</p>}
              </section>
            </div>}
        </QueryState>
      </div>

      <NewConversation orgId={orgId} open={composing} onClose={() => setComposing(false)} onCreated={id => setParams({
      c: id
    })} data-id="element-3104" data-mp="1tdfjcdda3ns" />
    </div>;
}
function NewConversation({
  orgId,
  open,
  onClose,
  onCreated
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const {
    data: projects
  } = usePortalQuery(() => listProjects(orgId, {
    limit: 100
  }), [orgId]);
  const [subject, setSubject] = React.useState('');
  const [projectId, setProjectId] = React.useState('');
  const [body, setBody] = React.useState('');
  const create = usePortalMutation(createConversation);
  React.useEffect(() => {
    if (open) {
      setSubject('');
      setProjectId('');
      setBody('');
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const id = await create.run(orgId, {
      subject,
      projectId: projectId || null,
      body
    });
    if (id) {
      onClose();
      onCreated(id);
    }
  };
  return <Dialog open={open} onClose={onClose} title="New conversation" description="The team is notified straight away." data-id="element-3105" data-mp="oofi9odpxauc">
      <form onSubmit={submit} className="space-y-5" data-id="element-3106" data-mp="jrgppa9pdby9">
        <Field id="conv-subject" label="Subject" required error={create.error?.details.subject} data-id="element-3107" data-mp="2jsdi106g50uj">
          <input id="conv-subject" value={subject} onChange={e => setSubject(e.target.value)} className={inputClass} maxLength={300} data-id="element-3108" data-mp="2orhpnmdq2qir" />
        </Field>
        <Field id="conv-project" label="Project" data-id="element-3109" data-mp="2trw01b2sqcjy">
          <select id="conv-project" value={projectId} onChange={e => setProjectId(e.target.value)} className={inputClass} data-id="element-3110" data-mp="2xs68qvhz3h2i">
            <option value="" data-id="element-3111" data-mp="32pxz3l2yyv4l">Not about a specific project</option>
            {projects?.data.map(p => <option key={p.id} value={p.id} data-id="element-3112" data-mp="37n7tzg94nyzt">
                {p.name}
              </option>)}
          </select>
        </Field>
        <Field id="conv-body" label="Message" required error={create.error?.details.body} data-id="element-3113" data-mp="3hfm1kpmvzztw">
          <textarea id="conv-body" rows={5} value={body} onChange={e => setBody(e.target.value)} className={`${inputClass} resize-y`} maxLength={5000} data-id="element-3114" data-mp="3lh7iqn6gf09w" />
        </Field>
        {create.error && !create.error.details.subject && !create.error.details.body && <InlineError error={create.error} data-id="element-3115" data-mp="1etqdplxqfnrf" />}
        <div className="flex gap-3" data-id="element-3116" data-mp="19tlydz8k8z2s">
          <Button type="submit" disabled={create.pending} data-id="element-3117" data-mp="19skgpbx1hec3">
            {create.pending ? 'Sending' : 'Send'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-3118" data-mp="15tuzf88vrcn6">
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>;
}